import {
  ConfiguredInstallationState,
  ExistingInstallationSummary,
  InstallationCartOption,
  InstallationPreferences,
} from 'types/installation';

export const INSTALLATION_CART_OPTIONS: readonly InstallationCartOption[] = [
  {
    value: 'keep-existing',
    label: 'Keep current installation',
    description: 'Keep the cart experience already configured for your store.',
  },
  {
    value: 'cart-widget',
    label: 'Cart Widget',
    description:
      'Offer the delivery guarantee inline and keep your checkout button.',
  },
  {
    value: 'checkout-plus-buttons',
    label: 'Checkout Plus — two buttons',
    description:
      'Show checkout buttons with and without the delivery guarantee.',
  },
  {
    value: 'checkout-plus-link',
    label: 'Checkout Plus — button + link',
    description:
      'Show one checkout button with a link to add or remove the delivery guarantee.',
  },
  {
    value: 'none',
    label: 'No cart offer',
    description:
      'Plan a checkout-only or merchant-paid experience without a cart offer.',
  },
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isOneOf = <T extends string>(
  value: unknown,
  choices: readonly T[],
): value is T =>
  typeof value === 'string' && choices.some((choice) => choice === value);

const isOptionalChoice = (value: unknown): value is boolean | null =>
  value === null || typeof value === 'boolean';

export const createInstallationPreferences = (): InstallationPreferences => ({
  schemaVersion: 1,
  status: 'draft',
  payer: 'keep-existing',
  cartExperience: 'keep-existing',
  addToCart: null,
  checkoutExtension: null,
  installationMethod: 'keep-existing',
  placement: 'keep-existing',
  appearance: { mode: 'preserve-existing' },
});

export const parseInstallationPreferences = (
  value: unknown,
): InstallationPreferences | null => {
  if (
    !isRecord(value) ||
    value.schemaVersion !== 1 ||
    value.status !== 'draft' ||
    !isOneOf(value.payer, ['keep-existing', 'customer', 'merchant'] as const) ||
    !isOneOf(value.cartExperience, [
      'keep-existing',
      'cart-widget',
      'checkout-plus-buttons',
      'checkout-plus-link',
      'none',
    ] as const) ||
    !isOptionalChoice(value.addToCart) ||
    !isOptionalChoice(value.checkoutExtension) ||
    !isOneOf(value.installationMethod, [
      'keep-existing',
      'theme',
      'headless',
      'assisted',
    ] as const) ||
    !isOneOf(value.placement, [
      'keep-existing',
      'cart-page',
      'cart-drawer',
      'cart-page-and-drawer',
    ] as const) ||
    !isRecord(value.appearance) ||
    !isOneOf(value.appearance.mode, [
      'inherit-theme',
      'preserve-existing',
    ] as const)
  ) {
    return null;
  }

  // Explicit projection keeps live settings and selectors out of the draft.
  return {
    schemaVersion: 1,
    status: 'draft',
    payer: value.payer,
    cartExperience: value.cartExperience,
    addToCart: value.addToCart,
    checkoutExtension: value.checkoutExtension,
    installationMethod: value.installationMethod,
    placement: value.placement,
    appearance: { mode: value.appearance.mode },
  };
};

const configuredState = (value: unknown): ConfiguredInstallationState => {
  if (!isRecord(value) || typeof value.enabled !== 'boolean') return 'unknown';
  return value.enabled ? 'enabled' : 'disabled';
};

export const summarizeExistingInstallation = (
  store: unknown,
): ExistingInstallationSummary => ({
  checkoutPlus: configuredState(
    isRecord(store) ? store.checkoutPlus : undefined,
  ),
  addToCart: configuredState(isRecord(store) ? store.addToCartPlus : undefined),
});
